import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BackHandOutlinedIcon from '@mui/icons-material/BackHandOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import highlightsImage from '../../static/images/marketing/head.png';

const items = [
  {
    icon: <BackHandOutlinedIcon sx={{ color: '#5d5d5d' }} />,
    title: 'Automatic',
    description:
      '3D models are automatically generated from MRI images using our patented AI methods, '
      + 'allowing you to independently create your models without the need for external '
      + 'expertise.',
  },
  {
    icon: <RocketLaunchOutlinedIcon />,
    title: 'Fast',
    description:
      'In under 2 hours, your 3D models are ready, making them easy to integrate into your daily routine.',
  },
  {
    icon: <VisibilityOutlinedIcon />,
    title: 'Intuitive',
    description:
      'The user-friendly interface, accessible directly from any web browser, allows you to '
      + 'easily make modifications to the 3D models, add annotations, and collaborate in '
      + 'real-time with the radiologist who performed the scan.',
  },
];

export default function Highlights() {
  const [isHighlightsImageLoaded, setIsHighlightsImageLoaded] = React.useState(false);
  const showTextSkeletons = !isHighlightsImageLoaded;

  React.useEffect(() => {
    const image = new Image();
    image.src = highlightsImage;
    image.onload = () => setIsHighlightsImageLoaded(true);
  }, []);

  return (
    <Box
      id="highlights"
      sx={{
        pt: { xs: 2, sm: 3 },
        pb: { xs: 6, sm: 10 },
        bgcolor: 'background.default',
        color: 'text.primary',
        overflowX: 'hidden',
      }}
    >
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: { xs: 2.5, sm: 4 },
        }}
      >
        <Typography
          component="h1"
          variant="h2"
          sx={{
            color: '#333333',
            letterSpacing: '0.2px',
            fontSize: { xs: '1.6rem', sm: '2.25rem' },
          }}
        >
          Highlights
        </Typography>
        <Card
          elevation={0}
          sx={{
            borderRadius: { xs: 4, sm: 6 },
            overflow: 'hidden',
            backgroundColor: '#b8cf9a',
            backgroundImage: {
              xs: 'none',
              sm: isHighlightsImageLoaded ? `url(${highlightsImage})` : 'none',
            },
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: { sm: '50%' },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              minHeight: { xs: 'auto', sm: 240, md: 340 },
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'center',
              textAlign: { xs: 'left', sm: 'center' },
              px: { xs: 2, sm: 8 },
              py: { xs: 2, sm: 5 },
              position: 'relative',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 2, sm: 0 },
            }}
          >
            {showTextSkeletons ? (
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="text" width="90%" height={32} />
                <Skeleton variant="text" width="80%" height={32} />
                <Skeleton variant="text" width="70%" height={32} />
              </Box>
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: '#333333',
                  lineHeight: { xs: 1.4, sm: 1.65 },
                  fontWeight: 400,
                  letterSpacing: '0.7px',
                  fontSize: { xs: '0.92rem', sm: '1.45rem', md: '1.85rem' },
                  maxWidth: { xs: '100%', sm: '80%', md: '95%' },
                  mx: { xs: 0, sm: 'auto' },
                  pr: { xs: 0, sm: 0 },
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {'Replico develops a powerful SaaS solution that empowers radiologists and '
                  + 'surgeons to effortlessly model and visualize their patients\' anatomy in 3D'}
              </Typography>
            )}
            <Box
              component="img"
              src={highlightsImage}
              alt="Replico highlights"
              loading="lazy"
              onLoad={() => setIsHighlightsImageLoaded(true)}
              sx={{
                display: { xs: 'block', sm: 'none' },
                width: '100%',
                maxWidth: 280,
                height: 'auto',
                maxHeight: 200,
                alignSelf: 'center',
                objectFit: 'contain',
                opacity: isHighlightsImageLoaded ? 1 : 0,
                transition: 'opacity 200ms ease',
              }}
            />
            {!isHighlightsImageLoaded && (
              <Skeleton
                variant="rectangular"
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  width: '100%',
                  maxWidth: 280,
                  height: 200,
                  alignSelf: 'center',
                }}
              />
            )}
          </Box>
        </Card>
        <Grid
          container
          spacing={{ xs: 2, sm: 3 }}
          sx={{ mt: { xs: 2, sm: 3 } }}
        >
          {items.map((item) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.title}>
              <Stack
                direction="column"
                component={Card}
                spacing={{ xs: 0.75, sm: 1 }}
                useFlexGap
                sx={(theme) => ({
                  color: 'text.primary',
                  p: { xs: 2.5, sm: 3.5 },
                  height: '100%',
                  borderRadius: 5,
                  backgroundColor: '#f5f5f5',
                  boxShadow: '0 8px 18px rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: (theme.vars || theme).palette.action.hover,
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none',
                    zIndex: 0,
                  },
                  '&:hover::after': {
                    opacity: 1,
                  },
                  '& > *': {
                    position: 'relative',
                    zIndex: 1,
                  },
                })}
              >
                {showTextSkeletons ? (
                  <Box sx={{ width: '100%' }}>
                    <Skeleton variant="text" width="60%" height={24} />
                    <Skeleton variant="text" width="90%" height={20} />
                    <Skeleton variant="text" width="85%" height={20} />
                  </Box>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      spacing={{ xs: 1, sm: 1.5 }}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          color: '#5d5d5d',
                          '& svg': { fontSize: { xs: 32, sm: 40 } },
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#333333',
                          lineHeight: 1.2,
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: { xs: '1rem', sm: '1.15rem' },
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b6b6b',
                        lineHeight: 1.5,
                        textAlign: 'left',
                        width: '100%',
                        mt: { xs: 0.75, sm: 1 },
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      }}
                    >
                      {item.description}
                    </Typography>
                  </>
                )}
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
